import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { LazyMotion, domAnimation } from "framer-motion";

// Lazy-load toast/tooltip providers — they pull in sonner, radix-toast and floating-ui
// which are not needed for first paint and add ~10KB+ of unused JS to the initial bundle.
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const TooltipProvider = lazy(() =>
  import("@/components/ui/tooltip").then(m => ({ default: m.TooltipProvider }))
);

// Lazy-load all routes (including landing) to reduce initial JS bundle
const Index = lazy(() => import("./pages/Index"));
const CareerPage = lazy(() => import("./pages/CareerPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const GuidePage = lazy(() => import("./pages/GuidePage"));
const CoursesPage = lazy(() => import("./pages/CoursesPage"));
const BrandPage = lazy(() => import("./pages/BrandPage"));
const IconPreview = lazy(() => import("./pages/IconPreview"));
const HumanIcons = lazy(() => import("./pages/HumanIcons"));
const CVAnalysis = lazy(() => import("./pages/CVAnalysis"));
const ProgrammeEvaluator = lazy(() => import("./pages/ProgrammeEvaluator"));
const LaborDashboard = lazy(() => import("./pages/LaborDashboard"));
const SkillsReport = lazy(() => import("./pages/SkillsReport"));
const FutureSkills = lazy(() => import("./pages/FutureSkills"));
const Upskilling = lazy(() => import("./pages/Upskilling"));
const FreeCourses = lazy(() => import("./pages/FreeCourses"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
    <QueryClientProvider client={queryClient}>
      <LazyMotion features={domAnimation} strict>
      <Suspense fallback={null}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/careers/:id" element={<CareerPage />} />
              <Route path="/guides/:id" element={<GuidePage />} />
              <Route path="/courses" element={<CoursesPage />} />
              <Route path="/courses/:category" element={<CoursesPage />} />
              <Route path="/brand" element={<BrandPage />} />
              <Route path="/icon-preview" element={<IconPreview />} />
              <Route path="/human-icons" element={<HumanIcons />} />
              <Route path="/cv-skills-analysis" element={<CVAnalysis />} />
              <Route path="/cv-analysis" element={<CVAnalysis />} />
              <Route path="/programme-evaluator" element={<ProgrammeEvaluator />} />
              <Route path="/labor-dashboard" element={<LaborDashboard />} />
              <Route path="/skills-report" element={<SkillsReport />} />
              <Route path="/future-skills" element={<FutureSkills />} />
              <Route path="/upskilling" element={<Upskilling />} />
              <Route path="/reskilling" element={<Upskilling />} />
              <Route path="/free-courses" element={<FreeCourses />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </Suspense>
      </LazyMotion>
    </QueryClientProvider>
  </ThemeProvider>
  </HelmetProvider>
);

export default App;
